/**
 * Biometric storage helpers for Zoobook 3.
 *
 * Stores the refresh token so the user can unlock the app with biometrics
 * (WebAuthn platform authenticator) instead of typing their password every day.
 *
 * - Web: localStorage (encrypted at rest by the browser/OS — acceptable for
 *   refresh tokens with short TTL + rotation).
 * - Electron: IPC to main process using safeStorage (OS keychain encryption).
 *
 * IMPORTANT: Access tokens are NEVER stored — only refresh tokens.
 */

const STORAGE_KEY = 'zbk3_refresh_token';

interface ElectronBridge {
  invoke: (channel: string, ...args: unknown[]) => Promise<unknown>;
  on: (channel: string, callback: (...args: unknown[]) => void) => () => void;
  safeStorage: {
    store: (key: string, value: string) => Promise<void>;
    retrieve: (key: string) => Promise<string | null>;
    remove: (key: string) => Promise<void>;
  };
}

declare global {
  interface Window {
    zoobook?: ElectronBridge;
  }
}

function isElectron(): boolean {
  return typeof window !== 'undefined' && typeof window.zoobook?.safeStorage !== 'undefined';
}

/**
 * Store a refresh token for biometric unlock.
 *
 * In Electron, uses safeStorage (OS keychain). In browser, uses localStorage.
 */
export async function storeRefreshToken(token: string): Promise<void> {
  if (isElectron()) {
    await window.zoobook?.safeStorage.store(STORAGE_KEY, token);
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
    console.warn('Failed to store refresh token in localStorage');
  }
}

/**
 * Retrieve the stored refresh token.
 */
export async function getStoredRefreshToken(): Promise<string | null> {
  if (isElectron()) {
    return window.zoobook?.safeStorage.retrieve(STORAGE_KEY) ?? null;
  }
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Remove the stored refresh token (on explicit sign-out or token revocation).
 */
export async function clearStoredRefreshToken(): Promise<void> {
  if (isElectron()) {
    await window.zoobook?.safeStorage.remove(STORAGE_KEY);
    return;
  }
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore — nothing to clear
  }
}

/**
 * Check if a platform authenticator (Touch ID / Windows Hello / Face ID) is available.
 * This is used to decide whether to show the "Enable biometric unlock" option.
 */
export async function isBiometricAvailable(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (typeof window.PublicKeyCredential === 'undefined') return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}
